// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import "./styles.css";
import "@granite/editor/styles.css";

import { unwrap } from "@granite/api-client";
import { AutoTooltips, TooltipProvider } from "@granite/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { api, queryClient } from "./lib/api.js";
import { rememberWhereVisitorCameFrom } from "./lib/cameFrom.js";
import { setLocale } from "./lib/i18n.js";
import { isDesktop, overlayTitleBar } from "./lib/platform.js";
import { applyPreferences, preferences } from "./lib/preferences.js";
import { syncPreferences } from "./lib/preferencesSync.js";
import { initSession, sessionStore } from "./lib/session.js";
import { router } from "./router.js";

setLocale("en");
if (!isDesktop) rememberWhereVisitorCameFrom();
applyPreferences(preferences.getState());

// Production builds work offline: the service worker keeps the app's files (P3-T04).
if (import.meta.env.PROD && !isDesktop && "serviceWorker" in navigator) {
  void navigator.serviceWorker.register("/sw.js").catch((err: unknown) => {
    console.warn("service worker registration failed", err);
  });
}

// Re-run route guards when the user signs in or out (in this tab or another one).
let lastStatus = sessionStore.getState().status;
sessionStore.subscribe((s) => {
  if (s.status === lastStatus) return;
  lastStatus = s.status;
  if (s.status === "signedOut") queryClient.clear();
  void router.invalidate();
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("missing #root element");
const root = createRoot(rootElement);

if (overlayTitleBar) document.documentElement.dataset.titlebar = "overlay";
// The desktop app behaves like a native one: interface text is not selectable (styles.css),
// and the web view's own right-click menu (Open Link, Download Linked File, ...) shows only
// where it helps: in fields and notes (cut, copy, paste, spelling) and on selected text.
// Granite's own menus (the file tree's, say) open as before.
if (isDesktop) {
  document.documentElement.dataset.desktop = "true";
  document.addEventListener("contextmenu", (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (target?.closest('input, textarea, [contenteditable="true"], [contenteditable=""], .cm-editor')) return;
    if (window.getSelection()?.toString()) return;
    e.preventDefault();
  });
}

// The desktop app's About window: on its own, with no session, menus, or sync (a second
// session would rotate the main window's refresh token).
const aboutWindow = isDesktop && window.location.hash.startsWith("#/about");
if (aboutWindow) {
  void import("./routes/AboutPage.js").then(({ AboutPage }) => {
    root.render(
      <StrictMode>
        <AboutPage />
      </StrictMode>,
    );
  });
} else {
  startApp();
}

function startApp() {
  if (isDesktop) {
    void import("./platform/desktopBridge.js").then((m) => m.startDesktopBridge());
  }

  // Appearance follows the account to every device; the cached copy paints first.
  syncPreferences({
    preferences,
    session: sessionStore,
    load: async () => unwrap(await api.GET("/v1/me/preferences")),
    save: async (body) => unwrap(await api.PUT("/v1/me/preferences", { body })),
  });

  void initSession().then(() => {
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <AutoTooltips />
              <RouterProvider router={router} />
            </TooltipProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </StrictMode>,
    );
  });
}
